import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { UserModel } from '../models/user.model';
import { SessionService } from '../shared/session.service';
import { UtilsService } from '../shared/utils.service';
import { StorageService } from '../shared/storage.service';
import { AppService } from '../app.service';
import { ApiRequestService } from '../shared/api-request.service';
import { IndustriesModel } from '../admin/industries/industries.model';
import { TradeModel } from '../admin/trades/trades.model';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignUpComponent implements OnInit, AfterContentInit {
  user: UserModel = new UserModel();
  tos: boolean;

  inProgress: boolean = false;

  // Emitted after a successful registration so the parent (SignInComponent) can switch to the Sign In tab.
  @Output() registered = new EventEmitter<void>();

  industries: IndustriesModel[] = [];

  trades: TradeModel[] = [];

  passwordValidated: boolean = false;
  passwordFieldType: string = 'password';

  autoCompletedEntityNames: string[] = [];

  /**
   * Whether the current suggestion list is a "did you mean" fallback rather than real matches (SC-2507).
   *
   * The API sets is_suggestion when the search found nothing and it fell back to near matches. Presenting
   * those as though they were matches would invite the user to accept a company that merely resembles
   * theirs, so the list is labelled differently.
   */
  entityNameSuggestionMode: boolean = false;

  /**
   * Whether a search has completed for the term currently in the field and found nothing.
   *
   * Drives the "not found" hint. Deliberately separate from an empty list, which is also the state before
   * anybody has typed anything.
   */
  entityNameSearched: boolean = false;

  /**
   * Every company name the search has returned during this registration, lowercased (SC-2509).
   *
   * Deliberately separate from autoCompletedEntityNames, which is the visible dropdown and is replaced on
   * every search. A name the API returned once is an account that exists, and that does not stop being true
   * when the user edits the box, so the check for "did they choose a real company" reads this instead of the
   * current dropdown contents.
   */
  private knownAccountNames: Set<string> = new Set<string>();

  /**
   * The company name the user has explicitly agreed to create as a NEW company (SC-2509).
   *
   * Registration used to create an account for whatever was typed, with no indication that it was creating
   * one rather than joining one. That is the main source of duplicate accounts: somebody whose employer is
   * already on the platform types a slightly different name and silently becomes a second company.
   *
   * Holds the confirmed name rather than a boolean so that editing the field after confirming requires
   * confirming again. A yes to "create Acme Roofing" is not a yes to "create Acme Rooding".
   */
  private confirmedNewCompanyName: string | null = null;

  // Mobile field Country Code selector values
  mobile_input_id: string = 'phone-1';
  mobile_number: string = '';
  mobile_country_code: string = '';
  mobile_dial_code: string = '';
  mobile_e164: string = '';
  mobile_error_state: boolean = true;

  constructor(
    public router: Router,
    public sessionService: SessionService,
    public utils: UtilsService,
    private storage: StorageService,
    private app: AppService,
    private api: ApiRequestService
  ) {}

  ngOnInit() {
    // Get industries from the API.
    this.getIndustries();

    // Get trades from the API.
    this.getTrades();
  }

  // Debounce timer and delay for the entity name autocomplete.
  debounceTimer: any;
  debounceDelay: number = 300;

  /**
   * Gets the entity names from the API for the autocomplete.
   * @param search The search string.
   */
  getEntityNames(search: string): void {
    // Any edit invalidates a previous agreement to create this company (SC-2509).
    this.confirmedNewCompanyName = null;

    // Check if the search string is less than 3 characters.
    if (search.length < 3) {
      this.autoCompletedEntityNames.length = 0;
      this.entityNameSuggestionMode = false;
      this.entityNameSearched = false;
      return;
    }
    // Check if the debounce timer is set.
    if (this.debounceTimer) {
      // Clear the debounce timer.
      clearTimeout(this.debounceTimer);
    }
    // Set the debounce timer.
    this.debounceTimer = setTimeout(() => {
      // Make an API request to get the entity names.
      this.api
        .makeRequest(
          'get',
          'v2/global-account-search',
          {},
          {
            search: search,
            order_by: 'name',
            order_direction: 'asc'
          }
        )
        .then((response) => {
          // Ignore a response that arrived after the user carried on typing, so the list and the
          // "not found" hint always describe what is actually in the field.
          if (this.user.entity_name !== search) {
            return;
          }
          // Clear the current list of accounts and store the new ones.
          this.autoCompletedEntityNames.length = 0;
          this.autoCompletedEntityNames.push(
            ...response.data.map((record: { name: any }) => record.name)
          );
          // Remember these as accounts that exist, independently of the dropdown's contents.
          this.autoCompletedEntityNames.forEach((name: string) =>
            this.knownAccountNames.add((name || '').trim().toLowerCase())
          );
          this.entityNameSuggestionMode = Boolean(response.is_suggestion);
          this.entityNameSearched = true;
        });
    }, this.debounceDelay);
  }

  /**
   * Whether the name in the field is one the search actually returned.
   *
   * Used to decide if registration is about to JOIN a company or CREATE one. Compared case-insensitively
   * and ignoring surrounding whitespace, because a user who selects a suggestion and then adjusts its
   * capitalisation has still chosen that company.
   */
  private entityNameMatchesAKnownAccount(): boolean {
    const typed: string = (this.user.entity_name || '').trim().toLowerCase();

    if (!typed) {
      return false;
    }

    return this.knownAccountNames.has(typed);
  }

  ngAfterContentInit() {
    if (this.sessionService.isAuthenticated()) {
      this.router.navigate(['/']);
    }
  }

  private async getIndustries() {
    this.industries = await this.utils.getIndustries();
  }

  private async getTrades() {
    this.trades = await this.utils.getTrades();
  }

  /**
   * Get Output values from phone input component.
   */
  getOutputPhoneDetails($event) {
    if ($event) {
      let phone = $event;
      this.mobile_number =
        phone.hasOwnProperty('number') && phone.number !== null
          ? phone.number
          : '';
      this.mobile_country_code =
        phone.hasOwnProperty('countryCode') && phone.countryCode !== null
          ? phone.countryCode
          : '';
      this.mobile_dial_code =
        phone.hasOwnProperty('dialCode') && phone.dialCode !== null
          ? phone.dialCode
          : '';
      this.mobile_e164 =
        phone.hasOwnProperty('e164Number') && phone.e164Number !== null
          ? phone.e164Number
          : '';
      this.mobile_error_state =
        phone.hasOwnProperty('errorState') && phone.errorState !== null
          ? phone.errorState
          : true;
    }
  }

  /**
   * Submits the registration form to the API for user registration.
   * @param form
   */
  onSubmit(form: NgForm) {
    // Make sure all required input fields are filled in.
    if (!form.valid) {
      this.utils.showModal(
        'Error Message',
        'Please complete all required fields.'
      );
      return;
    }

    // Check Error states of phone input then continue submission
    if (this.mobile_error_state) {
      if (this.mobile_number.length < 3) {
        this.utils.showModal(
          'Error Message',
          'A Phone number must be at least 3 characters long.'
        );
      } else {
        this.utils.showModal(
          'Error Message',
          'Please complete all required fields.'
        );
      }
      return;
    }

    // Terminate early if another process is in progress.
    if (this.inProgress) {
      return;
    }

    // Do not create a new company without saying so (SC-2509).
    //
    // Registering with a name the search did not return creates a brand new company account and makes the
    // registrant its administrator. That is a legitimate and necessary path, because somebody whose employer
    // is genuinely not on the platform has to be able to sign up. It just must not happen silently: the
    // common case is that the employer IS here under a slightly different name, and the user has no way of
    // knowing that a second company is about to appear.
    //
    // Registration is not blocked, only acknowledged. Blocking it would strand exactly the users this flow
    // exists for.
    if (!this.entityNameMatchesAKnownAccount()) {
      const typedName: string = (this.user.entity_name || '').trim();

      if (this.confirmedNewCompanyName !== typedName) {
        this.utils.showModal(
          'Is this a new company?',
          '<p>We could not find <b>' +
            typedName +
            '</b> on SiteConnect.</p>' +
            (this.autoCompletedEntityNames.length
              ? '<p>Did you mean one of these?</p><ul><li>' +
                this.autoCompletedEntityNames.slice(0, 5).join('</li><li>') +
                '</li></ul>' +
                '<p>If so, close this and choose it from the list instead.</p>'
              : '') +
            '<p>Continuing will create <b>' +
            typedName +
            '</b> as a new company with you as its administrator. ' +
            'If your employer is already using SiteConnect, search for them instead so your records stay together.</p>',
          () => {
            // Remember the exact name that was agreed to, then run the same submit again. Editing the field
            // afterwards clears this and asks again.
            this.confirmedNewCompanyName = typedName;
            this.onSubmit(form);
          }
        );
        return;
      }
    }

    // Update User details with Mobile field Country Code selector values
    this.user.mobile = this.mobile_number;
    this.user.mobile_country_code = this.mobile_country_code;
    this.user.mobile_dial_code = this.mobile_dial_code;
    this.user.mobile_e164 = this.mobile_e164;

    // Change the in progress state to true.
    this.inProgress = true;
    // Make an API request to register the user.
    this.api
      .makeRequest('post', 'v2/register', this.user)
      .then((response) => {
        this.inProgress = false;
        // Registration does not sign the user in. They must verify their email address first. Show a
        // message telling them to check their email, reset the form, and ask the parent to switch back to
        // the Sign In tab.
        this.utils.showToast(
          response && response.message
            ? response.message
            : 'Your account has been created. Please check your email to verify your address before signing in.'
        );
        this.resetForm(form);
        this.registered.emit();
      })
      .catch((errors) => {
        this.inProgress = false;
        this.utils.handleAPIErrors(errors);
      });
  }

  /**
   * Resets the sign-up form and its bound state after a successful registration.
   */
  private resetForm(form: NgForm): void {
    form.resetForm();
    this.user = new UserModel();
    this.tos = false;
    this.passwordValidated = false;
    // Clear the company search state too, so a second registration in the same session starts from a blank
    // slate rather than inheriting the previous one's suggestions and its agreement to create a company.
    this.autoCompletedEntityNames.length = 0;
    this.entityNameSuggestionMode = false;
    this.entityNameSearched = false;
    this.confirmedNewCompanyName = null;
    this.knownAccountNames.clear();
    // Reset the mobile phone selector values.
    this.mobile_number = '';
    this.mobile_country_code = '';
    this.mobile_dial_code = '';
    this.mobile_e164 = '';
    this.mobile_error_state = true;
    // Clear any entity name autocomplete suggestions.
    this.autoCompletedEntityNames.length = 0;
  }

  getOutputPasswordValidated($event) {
    this.passwordValidated = $event;
  }

  /**
   * Toggles the visibility of the password field.
   *
   * @returns {void}
   */
  togglePasswordVisibility(): void {
    this.passwordFieldType =
      this.passwordFieldType === 'password' ? 'text' : 'password';
  }
}
